import { useTheme } from "@emotion/react";
import { formatCurrency, formatNumber } from "../utils/StatUtils";
import { Card, CardContainer } from "../../../../components/Card";
import { StatCard } from "../overview/OverviewHelper";
import { BarChart, LineChart, PieChart } from "../../../../components/Charts";

const PaymentsView = ({ filterBy, filteredData = [] }) => {
    const theme = useTheme() || {};

    const getCumulativeData = () => {
        const cumulative = [];
        let runningTotals = {};

        filteredData.forEach((week = {}) => {
            const cumulativeWeek = { ...week };
            const cumulativeFields = [
                "total_auto_paid",
                "total_manual_paid",
                "total_earners",
            ];
            cumulativeFields.forEach((field) => {
                const value = Number(week[field]) || 0;
                runningTotals[field] = (runningTotals[field] || 0) + value;
                cumulativeWeek[`${field}_Cumulative`] = runningTotals[field];
            });
            cumulative.push(cumulativeWeek);
        });

        return cumulative;
    };

    const dataToUse =
        filterBy === "cumulativeView" ? getCumulativeData() : filteredData;

    const enrichedData = dataToUse.map((entry = {}) => ({
        ...entry,
        TotalPaid:
            ((Number(entry?.total_auto_paid) || 0) +
                (Number(entry?.total_manual_paid) || 0)) /
            1000,
        TotalPaid_Cumulative:
            ((Number(entry?.total_auto_paid_Cumulative) || 0) +
                (Number(entry?.total_manual_paid_Cumulative) || 0)) /
            1000,
    }));

    const reduceField = (data, key) =>
        data.reduce((sum, item = {}) => sum + (Number(item[key]) || 0), 0);

    const totalAutoPaid = reduceField(dataToUse, "total_auto_paid");
    const totalManualPaid = reduceField(dataToUse, "total_manual_paid");
    const totalEarners = reduceField(dataToUse, "total_earners");

    const stats = [
        {
            key: "total_auto_paid",
            label: "Total Auto Paid",
            value: formatCurrency(totalAutoPaid),
            color: theme?.palette?.graph?.cyan || "#06b6d4",
        },
        {
            key: "total_manual_paid",
            label: "Total Manual Paid",
            value: formatCurrency(totalManualPaid),
            color: theme?.palette?.graph?.yellow || "#facc15",
        },
        {
            key: "total_earners",
            label: "Total Earners",
            value: formatNumber(totalEarners),
            color: theme?.palette?.graph?.magenta || "#d946ef",
        },
        {
            label: "Avg Earning per Creator",
            value: formatCurrency(
                totalEarners > 0
                    ? (
                          (totalAutoPaid + totalManualPaid) /
                          totalEarners
                      ).toFixed(2)
                    : 0
            ),
            color: theme?.palette?.graph?.green || "#22c55e",
        },
    ];

    return (
        <div>
            <CardContainer>
                {stats.map((stat, index) => (
                    <Card key={index}>
                        <StatCard
                            value={stat?.value}
                            label={stat?.label}
                            color={stat?.color}
                        />
                    </Card>
                ))}
            </CardContainer>

            <CardContainer>
                <LineChart
                    data={dataToUse}
                    series={
                        filterBy === "weeklyView"
                            ? [
                                  {
                                      name: "Auto Payments",
                                      key: "total_auto_paid",
                                  },
                                  {
                                      name: "Manual Payments",
                                      key: "total_manual_paid",
                                  },
                              ]
                            : [
                                  {
                                      name: "Auto Payments (Cumulative)",
                                      key: "total_auto_paid_Cumulative",
                                  },
                                  {
                                      name: "Manual Payments (Cumulative)",
                                      key: "total_manual_paid_Cumulative",
                                  },
                              ]
                    }
                    xKey="week"
                    title="Payment Trends"
                    yFormatter={formatCurrency}
                />

                <PieChart
                    data={dataToUse}
                    series={[
                        { key: "total_manual_paid", name: "Manual Payments" },
                        { key: "total_auto_paid", name: "Auto Payments" },
                    ]}
                    title="Payment Method Distribution"
                />
            </CardContainer>

            <CardContainer>
                <BarChart
                    data={enrichedData}
                    series={
                        filterBy === "weeklyView"
                            ? [
                                  {
                                      name: "Total Earners",
                                      key: "total_earners",
                                  },
                                  {
                                      name: "Total Paid (in Thousands)",
                                      key: "TotalPaid",
                                  },
                              ]
                            : [
                                  {
                                      name: "Total Earners (Cumulative)",
                                      key: "total_earners_Cumulative",
                                  },
                                  {
                                      name: "Total Paid (Cumulative)",
                                      key: "TotalPaid_Cumulative",
                                  },
                              ]
                    }
                    xKey="week"
                    title="Earners vs Total Payments"
                    yFormatter={formatCurrency}
                />
            </CardContainer>
        </div>
    );
};

export default PaymentsView;
